import { Icon } from '@iconify/react'
import { Empty, InputNumber, Select } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'

import { Button } from 'common/components/Button/Button'
import { Spinner } from 'common/components/Spinner'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { formatMoney } from 'common/services/format'
import { useAppDispatch } from 'common/hooks/redux'
import {
  procedureApi,
  useFetchEncounterCheckoutQuery,
  usePayEncounterMutation,
  useSaveCreditCardMutation,
} from 'features/Procedure/state/procedure.api'
import { ChargeType } from 'features/Procedure/state/procedure-api.type'
import { ProcedureStoreTagTypes } from 'features/Procedure/state/procedure.type'

import styles from './checkout.module.scss'
import { EncounterPaymentDialog } from './payment-dialog/encounter-payment-dialog'
import { buildPayEncounterBody } from './payment-dialog/encounter-payment-dialog.utils'
import type { CcOnFileValues } from './payments/cc-on-file-row'
import { PaymentResultDialog } from './receipt-dialog/payment-result-dialog'
import {
  getChargeErrorDescription,
  parsePayEncounterResponse,
  type PaymentResult,
} from './receipt-dialog/payment-result.utils'
import { buildCheckoutPayments } from './payments/checkout-payments.utils'
import { CreditCardGroup, type CreditCardPayment } from './payments/credit-card-group'
import { OtherPaymentGroup, type OtherPaymentValues } from './payments/other-payment-group'
import { PaymentMethod } from './payments/payment-method'
import { PaymentsSection } from './payments/payments-section'
import {
  applyDiscountToContext,
  applyPatientDefaultDiscounts,
  buildEncounterCheckoutPayload,
  calculateCheckoutPreview,
  CHECKOUT_DISCOUNT_SCOPE_OPTIONS,
  clampCreditAmounts,
  clearDiscountContext,
  deriveAppliedDiscountSelections,
  deriveContextDiscount,
  deriveInitialDiscountCategory,
  derivePerLineDiscountSelections,
  formatCheckoutDate,
  getEnabledDiscountContexts,
  mapDiscountCategoryToCheckoutScope,
  NO_DISCOUNT_OPTION_ID,
  pickDiscountContextSelections,
  type CheckoutCreditAmounts,
  type CheckoutDiscountScope,
  type CheckoutDiscountSelections,
} from './checkout.utils'

type CheckoutProps = {
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  disableActions?: boolean
}

const formatDiscountAmount = (amount: number): string =>
  amount > 0 ? `-${formatMoney(amount)}` : formatMoney(0)

const getDiscountAmountClassName = (amount: number): string | undefined =>
  amount > 0 ? styles.discountAmount : undefined

export const Checkout = ({
  selectedPatientId,
  selectedEncounterUid,
  disableActions = false,
}: CheckoutProps) => {
  const { siteId } = useParams<{ siteId: string }>()
  const [discountSelections, setDiscountSelections] = useState<CheckoutDiscountSelections>({})
  const [creditAmounts, setCreditAmounts] = useState<CheckoutCreditAmounts>({})
  const [discountCategory, setDiscountCategory] = useState<CheckoutDiscountScope>('all')
  const [productsDiscountId, setProductsDiscountId] = useState<string | null>(null)
  const [proceduresDiscountId, setProceduresDiscountId] = useState<string | null>(null)
  const [isMixedProducts, setIsMixedProducts] = useState(false)
  const [isMixedProcedures, setIsMixedProcedures] = useState(false)
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false)
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null)
  const [isPaymentResultOpen, setIsPaymentResultOpen] = useState(false)
  const [resumeFromPendingCharge, setResumeFromPendingCharge] = useState(false)
  const resumePromptedEncounterRef = useRef<string | null>(null)
  // Remembers a context's selections + select state when it gets disabled by a
  // category change, so it can be restored when the category re-enables it.
  const stashedDiscountRef = useRef<{
    products: {
      selections: CheckoutDiscountSelections
      optionId: string | null
      isMixed: boolean
    } | null
    procedures: {
      selections: CheckoutDiscountSelections
      optionId: string | null
      isMixed: boolean
    } | null
  }>({ products: null, procedures: null })

  const dispatch = useAppDispatch()
  const [saveCreditCard] = useSaveCreditCardMutation()
  const [payEncounterDirect, { isLoading: isCcCharging }] = usePayEncounterMutation()
  const [ccValues, setCcValues] = useState<CcOnFileValues>({
    cc_name: '',
    cc_number: '',
    cc_expiration: '',
    cc_cvv: '',
    cc_zip: '',
  })
  const [validatedCards, setValidatedCards] = useState<CreditCardPayment[]>([])
  const [otherPayment, setOtherPayment] = useState<OtherPaymentValues>({
    amount: '',
    reference: '',
  })
  const [chargeSnapshot, setChargeSnapshot] = useState<{
    credits: number
    tendered: number
  } | null>(null)
  const isChargingRef = useRef(false)

  const shouldSkipCheckoutQuery = !siteId || !selectedPatientId || !selectedEncounterUid

  const {
    data: checkoutData,
    isFetching,
    isLoading,
    isError,
  } = useFetchEncounterCheckoutQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedEncounterUid!,
    },
    {
      skip: shouldSkipCheckoutQuery,
    },
  )

  useEffect(() => {
    stashedDiscountRef.current = { products: null, procedures: null }
    if (!checkoutData) {
      setDiscountSelections({})
      setDiscountCategory('all')
      setProductsDiscountId(null)
      setProceduresDiscountId(null)
      setIsMixedProducts(false)
      setIsMixedProcedures(false)
      setCreditAmounts({})
      setValidatedCards([])
      setOtherPayment({ amount: '', reference: '' })
      setChargeSnapshot(null)
      return
    }

    const patientDefault = checkoutData.patient_default_discount
    const defaultScope = patientDefault ? mapDiscountCategoryToCheckoutScope(patientDefault) : 'all'
    const hasDefaultDiscount = Boolean(
      patientDefault?.discount_type_products || patientDefault?.discount_type_procedures,
    )

    const hasApplied =
      !checkoutData.has_per_line_discounts && checkoutData.applied_discounts.length > 0

    let nextSelections: CheckoutDiscountSelections
    if (checkoutData.has_per_line_discounts) {
      nextSelections = derivePerLineDiscountSelections({
        charges: checkoutData.charges,
        perLineDiscounts: checkoutData.per_line_discounts,
        patientDefault,
        defaultScope,
      })
    } else if (hasApplied) {
      nextSelections = deriveAppliedDiscountSelections({
        charges: checkoutData.charges,
        appliedDiscounts: checkoutData.applied_discounts,
        discountOptions: checkoutData.discounts,
      })
    } else if (hasDefaultDiscount) {
      nextSelections = applyPatientDefaultDiscounts({
        charges: checkoutData.charges,
        patientDefault,
        scope: defaultScope,
      })
    } else {
      nextSelections = {}
    }

    const products = deriveContextDiscount({
      charges: checkoutData.charges,
      selections: nextSelections,
      context: 'products',
    })
    const procedures = deriveContextDiscount({
      charges: checkoutData.charges,
      selections: nextSelections,
      context: 'procedures',
    })

    setDiscountSelections(nextSelections)
    setProductsDiscountId(products.optionId)
    setProceduresDiscountId(procedures.optionId)
    setIsMixedProducts(products.isMixed)
    setIsMixedProcedures(procedures.isMixed)
    setDiscountCategory(
      deriveInitialDiscountCategory({
        charges: checkoutData.charges,
        selections: nextSelections,
        patientDefault: checkoutData.patient_default_discount,
      }),
    )
    setCreditAmounts({})
    setValidatedCards([])
    setOtherPayment({ amount: '', reference: '' })
    setChargeSnapshot(null)
  }, [checkoutData])

  const typeOptions = useMemo(
    () =>
      (checkoutData?.discounts ?? []).map((discount) => ({
        value: discount.option_id,
        label: discount.title,
      })),
    [checkoutData?.discounts],
  )

  const buildDiscountOptions = useCallback(
    (isMixed: boolean) => [
      { value: NO_DISCOUNT_OPTION_ID, label: isMixed ? '-----' : '-- None --' },
      ...typeOptions,
    ],
    [typeOptions],
  )

  const rowDiscountOptions = useMemo(() => buildDiscountOptions(false), [buildDiscountOptions])
  const productsDiscountOptions = useMemo(
    () => buildDiscountOptions(isMixedProducts),
    [buildDiscountOptions, isMixedProducts],
  )
  const proceduresDiscountOptions = useMemo(
    () => buildDiscountOptions(isMixedProcedures),
    [buildDiscountOptions, isMixedProcedures],
  )

  const enabledDiscountContexts = getEnabledDiscountContexts(discountCategory)

  const preview = useMemo(() => {
    if (!checkoutData) return null

    return calculateCheckoutPreview({
      data: checkoutData,
      discountSelections,
      creditAmounts,
    })
  }, [checkoutData, creditAmounts, discountSelections])
  const checkoutPayload = useMemo(
    () => (preview ? buildEncounterCheckoutPayload(preview) : null),
    [preview],
  )

  const hasBackendBillingActivity = Boolean(checkoutData?.summary.encounter_is_billed)
  const isControlsDisabled = !checkoutData || disableActions
  const isEncounterFullyBilled = Boolean(checkoutData?.summary.encounter_is_billed)
  const hasAnyUnlockedRow = Boolean(
    checkoutData?.charges.some((c) => c.billed !== 1 && c.code.trim().toUpperCase() !== 'S9999'),
  )
  const areGlobalDiscountControlsDisabled =
    isControlsDisabled || isEncounterFullyBilled || !hasAnyUnlockedRow
  const isRefetching = isFetching && !isLoading

  const validatedCardsTotal = useMemo(
    () => validatedCards.reduce((sum, card) => sum + card.amount, 0),
    [validatedCards],
  )

  const otherPaymentAmount = useMemo(() => {
    const parsed = Number.parseFloat(otherPayment.amount)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  }, [otherPayment.amount])

  const tenderedTotal = useMemo(
    () => validatedCardsTotal + otherPaymentAmount,
    [validatedCardsTotal, otherPaymentAmount],
  )

  const creditsAppliedTotal = useMemo(
    () =>
      Object.values(creditAmounts).reduce(
        (sum, value) => sum + (Number.isFinite(value) ? value : 0),
        0,
      ),
    [creditAmounts],
  )

  const handleProductsDiscountChange = useCallback(
    (value: string) => {
      if (!checkoutData) return
      const optionId = value === NO_DISCOUNT_OPTION_ID ? null : value
      setProductsDiscountId(optionId)
      setIsMixedProducts(false)
      setDiscountSelections((prev) =>
        applyDiscountToContext({
          charges: checkoutData.charges,
          selections: prev,
          optionId,
          context: 'products',
        }),
      )
    },
    [checkoutData],
  )

  const handleProceduresDiscountChange = useCallback(
    (value: string) => {
      if (!checkoutData) return
      const optionId = value === NO_DISCOUNT_OPTION_ID ? null : value
      setProceduresDiscountId(optionId)
      setIsMixedProcedures(false)
      setDiscountSelections((prev) =>
        applyDiscountToContext({
          charges: checkoutData.charges,
          selections: prev,
          optionId,
          context: 'procedures',
        }),
      )
    },
    [checkoutData],
  )

  const handleDiscountCategoryChange = useCallback(
    (category: CheckoutDiscountScope) => {
      setDiscountCategory(category)
      if (!checkoutData) return

      const charges = checkoutData.charges
      const prevEnabled = getEnabledDiscountContexts(discountCategory)
      const nextEnabled = getEnabledDiscountContexts(category)
      const stash = stashedDiscountRef.current

      let next = discountSelections

      // Products: stash + clear when disabled, restore when re-enabled.
      if (prevEnabled.products && !nextEnabled.products) {
        stash.products = {
          selections: pickDiscountContextSelections({
            charges,
            selections: next,
            context: 'products',
          }),
          optionId: productsDiscountId,
          isMixed: isMixedProducts,
        }
        next = clearDiscountContext({ charges, selections: next, context: 'products' })
        setProductsDiscountId(null)
        setIsMixedProducts(false)
      } else if (!prevEnabled.products && nextEnabled.products && stash.products) {
        next = { ...next, ...stash.products.selections }
        setProductsDiscountId(stash.products.optionId)
        setIsMixedProducts(stash.products.isMixed)
        stash.products = null
      }

      // Procedures: stash + clear when disabled, restore when re-enabled.
      if (prevEnabled.procedures && !nextEnabled.procedures) {
        stash.procedures = {
          selections: pickDiscountContextSelections({
            charges,
            selections: next,
            context: 'procedures',
          }),
          optionId: proceduresDiscountId,
          isMixed: isMixedProcedures,
        }
        next = clearDiscountContext({ charges, selections: next, context: 'procedures' })
        setProceduresDiscountId(null)
        setIsMixedProcedures(false)
      } else if (!prevEnabled.procedures && nextEnabled.procedures && stash.procedures) {
        next = { ...next, ...stash.procedures.selections }
        setProceduresDiscountId(stash.procedures.optionId)
        setIsMixedProcedures(stash.procedures.isMixed)
        stash.procedures = null
      }

      setDiscountSelections(next)
    },
    [
      checkoutData,
      discountCategory,
      discountSelections,
      productsDiscountId,
      proceduresDiscountId,
      isMixedProducts,
      isMixedProcedures,
    ],
  )

  const handleRowDiscountChange = useCallback((billingId: number, value: string) => {
    setDiscountSelections((prev) => ({
      ...prev,
      [billingId]: value === NO_DISCOUNT_OPTION_ID ? null : value,
    }))
  }, [])

  const handleCreditAmountChange = useCallback(
    (creditId: number, value: number | string | null) => {
      if (!checkoutData || !preview) return

      const amount = typeof value === 'number' ? value : Number(value ?? 0)
      const nextCreditAmounts = {
        ...creditAmounts,
        [creditId]: Number.isFinite(amount) ? amount : 0,
      }

      setCreditAmounts(
        clampCreditAmounts({
          creditAmounts: nextCreditAmounts,
          credits: checkoutData.credits,
          maxApplicableAmount: preview.totals.amountBeforeCredits,
        }),
      )
    },
    [checkoutData, creditAmounts, preview],
  )

  const handleReset = useCallback(() => {
    setDiscountSelections({})
    setCreditAmounts({})
    setDiscountCategory('all')
    setProductsDiscountId(null)
    setProceduresDiscountId(null)
    setIsMixedProducts(false)
    setIsMixedProcedures(false)
  }, [])

  const handleSaveCc = useCallback(
    async (values: CcOnFileValues) => {
      if (!siteId || !selectedPatientId) {
        return { success: false, error: 'Missing site or patient' }
      }
      try {
        const response = await saveCreditCard({
          siteId,
          patientId: selectedPatientId,
          body: values,
        }).unwrap()
        const failed = response?.success === false || response?.status === false
        return failed
          ? { success: false, error: response?.message ?? 'Save failed' }
          : { success: true, message: response?.message }
      } catch (error: unknown) {
        const message =
          error && typeof error === 'object' && 'data' in error
            ? (error as { data?: { message?: string } }).data?.message ?? 'Save failed'
            : 'Save failed'
        return { success: false, error: message }
      }
    },
    [saveCreditCard, selectedPatientId, siteId],
  )

  const handleAddValidatedCard = useCallback((card: CreditCardPayment) => {
    setValidatedCards((prev) => [...prev, card])
  }, [])

  const handleRemoveValidatedCard = useCallback((token: string) => {
    setValidatedCards((prev) => prev.filter((card) => card.token !== token))
  }, [])

  const handleCharge = useCallback(async () => {
    if (!checkoutPayload || !preview) return
    if (isChargingRef.current) return
    setResumeFromPendingCharge(false)

    const hasManualPayments = validatedCards.length > 0 || otherPaymentAmount > 0
    if (hasManualPayments && siteId && selectedPatientId && selectedEncounterUid) {
      isChargingRef.current = true
      setChargeSnapshot({ credits: creditsAppliedTotal, tendered: tenderedTotal })
      const payments = buildCheckoutPayments({
        cards: validatedCards.map((c) => ({
          token: c.token,
          amount: c.amount,
          last4: c.last4,
          brand: c.brand,
        })),
        other:
          otherPaymentAmount > 0
            ? { amount: otherPaymentAmount, reference: otherPayment.reference }
            : null,
      })
      const onlyOther =
        otherPaymentAmount > 0 && validatedCards.length === 0 && creditsAppliedTotal <= 0
      const chargeType = onlyOther ? ChargeType.Other : ChargeType.CreditCard
      setValidatedCards([])
      setOtherPayment({ amount: '', reference: '' })
      try {
        const response = await payEncounterDirect({
          siteId,
          patientId: selectedPatientId,
          encounterUid: selectedEncounterUid,
          body: buildPayEncounterBody(preview, 0, null, { payments, chargeType }),
        }).unwrap()
        const parsed = parsePayEncounterResponse(response)
        if (parsed) {
          setPaymentResult(parsed)
          setIsPaymentResultOpen(true)
        }
        dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.EncounterCheckout]))
      } catch (error: unknown) {
        setPaymentResult({ kind: 'error', message: getChargeErrorDescription(error) })
        setIsPaymentResultOpen(true)
        dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.EncounterCheckout]))
      } finally {
        isChargingRef.current = false
        setChargeSnapshot(null)
      }
      return
    }

    if (creditsAppliedTotal > 0 && siteId && selectedPatientId && selectedEncounterUid) {
      isChargingRef.current = true
      setChargeSnapshot({ credits: creditsAppliedTotal, tendered: tenderedTotal })
      try {
        const response = await payEncounterDirect({
          siteId,
          patientId: selectedPatientId,
          encounterUid: selectedEncounterUid,
          body: buildPayEncounterBody(preview, 0, null, {
            chargeType: ChargeType.ApplyCredit,
          }),
        }).unwrap()
        const parsed = parsePayEncounterResponse(response)
        if (parsed) {
          setPaymentResult(parsed)
          setIsPaymentResultOpen(true)
        }
        dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.EncounterCheckout]))
      } catch (error: unknown) {
        setPaymentResult({ kind: 'error', message: getChargeErrorDescription(error) })
        setIsPaymentResultOpen(true)
        dispatch(procedureApi.util.invalidateTags([ProcedureStoreTagTypes.EncounterCheckout]))
      } finally {
        isChargingRef.current = false
      }
      return
    }

    setIsPaymentDialogOpen(true)
  }, [
    checkoutPayload,
    creditsAppliedTotal,
    dispatch,
    otherPayment.reference,
    otherPaymentAmount,
    payEncounterDirect,
    preview,
    selectedEncounterUid,
    selectedPatientId,
    siteId,
    tenderedTotal,
    validatedCards,
  ])

  const handleClosePaymentDialog = useCallback(() => {
    setIsPaymentDialogOpen(false)
    setResumeFromPendingCharge(false)
  }, [])

  const handleClosePaymentResult = useCallback(() => {
    setIsPaymentResultOpen(false)
  }, [])

  useEffect(() => {
    if (!selectedEncounterUid) {
      resumePromptedEncounterRef.current = null
      return
    }
    if (resumePromptedEncounterRef.current === selectedEncounterUid) {
      return
    }
    if (!disableActions && checkoutData?.pending_charge) {
      resumePromptedEncounterRef.current = selectedEncounterUid
      setResumeFromPendingCharge(true)
      setIsPaymentDialogOpen(true)
    }
  }, [checkoutData?.pending_charge, selectedEncounterUid, disableActions])

  const dialogResume = useMemo(() => {
    if (!resumeFromPendingCharge || !checkoutData?.pending_charge) return null
    const pc = checkoutData.pending_charge
    return {
      amount: pc.amount,
      stripeTerminalId: pc.stripe_terminal_id,
      message: pc.message ?? undefined,
    }
  }, [resumeFromPendingCharge, checkoutData?.pending_charge])

  // Rendered in every early return too: receipt_html only exists in the POST
  // response, so a failed refetch after payment must not unmount this dialog.
  const paymentResultDialog = (
    <PaymentResultDialog
      open={isPaymentResultOpen && paymentResult !== null}
      result={paymentResult}
      onClose={handleClosePaymentResult}
    />
  )

  if (shouldSkipCheckoutQuery) {
    return (
      <>
        <div className={styles.stateContainer}>
          <Empty description='Select a patient and encounter' />
        </div>
        {paymentResultDialog}
      </>
    )
  }

  if (isLoading) {
    return (
      <>
        <div className={styles.stateContainer}>
          <Spinner isLoading />
        </div>
        {paymentResultDialog}
      </>
    )
  }

  if (isError) {
    return (
      <>
        <div className={styles.stateContainer}>
          <Empty description='Failed to load checkout' />
        </div>
        {paymentResultDialog}
      </>
    )
  }

  if (!checkoutData || !preview) {
    return (
      <>
        <div className={styles.stateContainer}>
          <Empty description='No checkout details' />
        </div>
        {paymentResultDialog}
      </>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.contentColumn}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <span>Encounter Charges</span>
            {hasBackendBillingActivity && <span className={styles.billedBadge}>BILLED</span>}
          </div>

          <div className={styles.globalControls}>
            <div className={styles.fieldGroup}>
              <span>Discount</span>
              <Select
                className={styles.controlSelect}
                value={discountCategory}
                options={CHECKOUT_DISCOUNT_SCOPE_OPTIONS}
                disabled={areGlobalDiscountControlsDisabled}
                onChange={handleDiscountCategoryChange}
              />
            </div>
            <div className={styles.fieldGroup}>
              <span>Products</span>
              <Select
                className={styles.controlSelect}
                value={productsDiscountId ?? NO_DISCOUNT_OPTION_ID}
                options={productsDiscountOptions}
                disabled={areGlobalDiscountControlsDisabled || !enabledDiscountContexts.products}
                onChange={handleProductsDiscountChange}
              />
            </div>
            <div className={styles.fieldGroup}>
              <span>Procedures</span>
              <Select
                className={styles.controlSelect}
                value={proceduresDiscountId ?? NO_DISCOUNT_OPTION_ID}
                options={proceduresDiscountOptions}
                disabled={areGlobalDiscountControlsDisabled || !enabledDiscountContexts.procedures}
                onChange={handleProceduresDiscountChange}
              />
            </div>
            <div className={styles.globalDiscountAmount}>
              <span>Discount</span>
              <strong className={getDiscountAmountClassName(preview.totals.previewDiscounts)}>
                {formatDiscountAmount(preview.totals.previewDiscounts)}
              </strong>
            </div>
          </div>

          {preview.charges.length === 0 ? (
            <div className={styles.emptyPanel}>
              <Empty description='No charges found' />
            </div>
          ) : (
            <div className={styles.tableScroller}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Subtotal</th>
                    <th>Discount</th>
                    <th>Tax</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.charges.map((item) => (
                    <tr
                      key={item.charge.billing_id}
                      className={item.discountAmount > 0 ? styles.discountedRow : undefined}>
                      <td>
                        <div className={styles.serviceCell}>
                          <span className={styles.serviceTitle}>
                            {item.charge.code} - {item.charge.description}
                          </span>
                          <span className={styles.serviceMeta}>
                            <span className={styles.typeBadge}>
                              {item.charge.is_product ? 'Product' : 'Procedure'}
                            </span>
                            <span>Qty {item.charge.units}</span>
                            {item.charge.provider_name && <span>{item.charge.provider_name}</span>}
                          </span>
                        </div>
                      </td>
                      <td>{formatMoney(item.subtotal)}</td>
                      <td>
                        {item.appliedDiscountLabel !== null ? (
                          <span className={styles.appliedDiscountLabel}>
                            {item.appliedDiscountLabel}
                          </span>
                        ) : (
                          <div className={styles.discountCell}>
                            <Select
                              className={styles.rowDiscountSelect}
                              value={
                                discountSelections[item.charge.billing_id] ?? NO_DISCOUNT_OPTION_ID
                              }
                              options={rowDiscountOptions}
                              disabled={item.charge.billed === 1 || isControlsDisabled}
                              onChange={(value) =>
                                handleRowDiscountChange(item.charge.billing_id, value)
                              }
                            />
                            <span className={getDiscountAmountClassName(item.discountAmount)}>
                              {formatDiscountAmount(item.discountAmount)}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>{formatMoney(item.tax)}</td>
                      <td>
                        <strong>{formatMoney(item.total)}</strong>
                      </td>
                    </tr>
                  ))}
                  {preview.totals.totalTax > 0 && (
                    <tr className={styles.taxRow}>
                      <td>
                        <span className={styles.taxRowLabel}>
                          {checkoutData.summary.tax_code_text || 'Sales tax'}
                        </span>
                      </td>
                      <td />
                      <td />
                      <td />
                      <td>
                        <span className={styles.taxRowAmount}>
                          {formatMoney(preview.totals.totalTax)}
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Original Balance</td>
                    <td>{formatMoney(preview.totals.chargesSubtotal)}</td>
                    <td className={getDiscountAmountClassName(preview.totals.totalAdjustments)}>
                      {formatDiscountAmount(preview.totals.totalAdjustments)}
                    </td>
                    <td>{formatMoney(preview.totals.totalTax)}</td>
                    <td>{formatMoney(preview.totals.originalBalance)}</td>
                  </tr>
                  {preview.totals.previousTotal > 0 && (
                    <tr>
                      <td>Previous Payments/Credits/GC</td>
                      <td />
                      <td />
                      <td />
                      <td className={getDiscountAmountClassName(preview.totals.previousTotal)}>
                        {formatDiscountAmount(preview.totals.previousTotal)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>Outstanding Balance</td>
                    <td />
                    <td />
                    <td />
                    <td>
                      <strong>{formatMoney(preview.totals.amountBeforeCredits)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        {preview.credits.length === 0 ? (
          <section className={`${styles.panel} ${styles.panelDisabled}`}>
            <div className={styles.panelHeader}>
              <span>Apply Existing Credit</span>
              <span className={styles.panelHeaderHint}>No credits available</span>
            </div>
          </section>
        ) : (
          <section className={styles.panel}>
            <div className={styles.panelHeader}>Apply Existing Credit</div>
            <div className={styles.tableScroller}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Credit Source</th>
                    <th>Apply Amount</th>
                    <th>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.credits.map((item) => (
                    <tr key={item.credit.credit_id}>
                      <td>
                        <div className={styles.serviceCell}>
                          <span className={styles.serviceTitle}>
                            {formatMoney(item.availableAmount)} {item.credit.source_type}
                          </span>
                          <span className={styles.serviceMeta}>
                            <span>{item.credit.note}</span>
                            <span>{formatCheckoutDate(item.credit.created_date)}</span>
                          </span>
                        </div>
                      </td>
                      <td>
                        <InputNumber
                          className={styles.creditInput}
                          controls={false}
                          precision={2}
                          min={0}
                          max={item.availableAmount}
                          prefix='$'
                          value={item.appliedAmount}
                          disabled={isControlsDisabled}
                          onChange={(value) =>
                            handleCreditAmountChange(item.credit.credit_id, value)
                          }
                        />
                      </td>
                      <td>{formatMoney(item.remainingAfterApply)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <PaymentsSection title='Payments (Physical Card Not Present)'>
          <PaymentMethod
            icon='ph:credit-card-fill'
            title='Credit Card'
            hint='Validate one or more cards'
            badgeCount={validatedCards.length}
            defaultOpen>
            <CreditCardGroup
              onFile={checkoutData.credit_card}
              ccValues={ccValues}
              onCcChange={setCcValues}
              onSaveCc={handleSaveCc}
              maxAmount={preview.totals.amountDue}
              validated={validatedCards}
              onAddValidated={handleAddValidatedCard}
              onRemoveValidated={handleRemoveValidatedCard}
              disabled={isControlsDisabled}
              stripeKey={checkoutData.stripe_key}
            />
          </PaymentMethod>

          <PaymentMethod icon='ph:note-pencil-fill' title='Other' hint='Check or other reference'>
            <OtherPaymentGroup
              values={otherPayment}
              onChange={setOtherPayment}
              disabled={isControlsDisabled}
            />
          </PaymentMethod>
        </PaymentsSection>
      </div>

      <aside className={styles.summaryPanel}>
        <div className={styles.summaryHeader}>
          <span>Summary</span>
          {isRefetching && (
            <span className={styles.refreshIndicator} aria-live='polite'>
              <Icon icon='ph:circle-notch-bold' className={styles.refreshSpinner} />
              Updating
            </span>
          )}
        </div>

        {(() => {
          const displayCredits = chargeSnapshot?.credits ?? creditsAppliedTotal
          const displayTendered = chargeSnapshot?.tendered ?? tenderedTotal
          if (displayCredits <= 0 && displayTendered <= 0) return null
          return (
            <div className={styles.summaryRows}>
              {displayCredits > 0 && (
                <div>
                  <span>Existing Credits Used:</span>
                  <strong>{formatMoney(displayCredits)}</strong>
                </div>
              )}
              {displayTendered > 0 && (
                <div>
                  <span>Total Tendered:</span>
                  <strong>{formatMoney(displayTendered)}</strong>
                </div>
              )}
            </div>
          )
        })()}
        <div className={styles.amountDueRow}>
          <span>AMOUNT DUE:</span>
          <strong>{formatMoney(preview.totals.amountDue)}</strong>
        </div>
        <div className={styles.outstandingRow}>
          <span>OUTSTANDING:</span>
          <strong>{formatMoney(preview.totals.outstanding)}</strong>
        </div>

        <div className={styles.summaryActions}>
          <Button
            disabled={isCcCharging || disableActions}
            proportion={BUTTON_PROPORTION.LARGE}
            className={styles.chargeButton}
            onClick={handleCharge}
            icon={<Icon icon='ph:check-bold' />}>
            Charge
          </Button>
          <Button
            modifier={BUTTON_MODIFIER.SECONDARY}
            proportion={BUTTON_PROPORTION.LARGE}
            disabled={hasBackendBillingActivity || disableActions}
            onClick={handleReset}
            icon={<Icon icon='ph:x-bold' />}>
            Cancel
          </Button>
        </div>
      </aside>
      {preview && siteId && selectedPatientId && selectedEncounterUid && (
        <EncounterPaymentDialog
          open={isPaymentDialogOpen}
          onClose={handleClosePaymentDialog}
          resume={dialogResume}
          siteId={siteId}
          patientId={selectedPatientId}
          encounterUid={selectedEncounterUid}
          preview={preview}
        />
      )}
      {paymentResultDialog}
    </div>
  )
}
